/**
 * Utility functions for consistent date formatting across the application
 */

/**
 * Formats a date to dd/mm/yyyy format
 * @param {Date|string|object} date - The date to format (can be Date object, string, or Firestore timestamp)
 * @returns {string} - Formatted date string in dd/mm/yyyy format
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle different date types
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date?.toDate && typeof date.toDate === 'function') {
    // Firestore timestamp
    dateObj = date.toDate();
  } else if (date?.seconds) {
    // Firestore timestamp object
    dateObj = new Date(date.seconds * 1000);
  } else {
    dateObj = new Date(date);
  }
  
  // Check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  // Format as dd/mm/yyyy
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Formats a date with time to dd/mm/yyyy HH:mm format
 * @param {Date|string|object} date - The date to format
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle different date types
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date?.toDate && typeof date.toDate === 'function') {
    // Firestore timestamp
    dateObj = date.toDate();
  } else if (date?.seconds) {
    // Firestore timestamp object
    dateObj = new Date(date.seconds * 1000);
  } else {
    dateObj = new Date(date);
  }
  
  // Check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  // Format as dd/mm/yyyy HH:mm
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Formats a date for display with month name
 * @param {Date|string|object} date - The date to format
 * @returns {string} - Formatted date string (e.g., "15 January 2024")
 */
export const formatDateLong = (date) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle different date types
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date?.toDate && typeof date.toDate === 'function') {
    // Firestore timestamp
    dateObj = date.toDate();
  } else if (date?.seconds) {
    // Firestore timestamp object
    dateObj = new Date(date.seconds * 1000);
  } else {
    dateObj = new Date(date);
  }
  
  // Check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const day = dateObj.getDate();
  const month = months[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  
  return `${day} ${month} ${year}`;
};

/**
 * Formats a date relative to today (e.g., "Today", "Yesterday", "2 days ago")
 * @param {Date|string|object} date - The date to format
 * @returns {string} - Relative date string or dd/mm/yyyy if older than 7 days
 */
export const formatDateRelative = (date) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle different date types
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date?.toDate && typeof date.toDate === 'function') {
    // Firestore timestamp
    dateObj = date.toDate();
  } else if (date?.seconds) {
    // Firestore timestamp object
    dateObj = new Date(date.seconds * 1000);
  } else {
    dateObj = new Date(date);
  }
  
  // Check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateToCompare = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  
  if (dateToCompare.getTime() === today.getTime()) {
    return 'Today';
  } else if (dateToCompare.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    const daysDiff = Math.floor((today - dateToCompare) / (1000 * 60 * 60 * 24));
    if (daysDiff > 0 && daysDiff <= 7) {
      return `${daysDiff} day${daysDiff === 1 ? '' : 's'} ago`;
    } else {
      return formatDate(dateObj);
    }
  }
};